import { createTheme } from "@mui/material/styles";

// Extend the theme to include custom properties
declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      transitions: {
        standard: string;
        fast: string;
        slow: string;
        medium: string;
        easeInOut: string;
      };
      shadows: {
        card: string;
        cardHover: string;
        subtle: string;
        medium: string;
      };
      overlays: {
        // Black overlays
        light: string;
        medium: string;
        dark: string;
        darker: string;
        // White overlays
        whiteLight: string;
        whiteMedium: string;
        whiteStrong: string;
        whiteAlmost: string;
        whiteFull: string;
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      transitions?: {
        standard?: string;
        fast?: string;
        slow?: string;
        medium?: string;
        easeInOut?: string;
      };
      shadows?: {
        card?: string;
        cardHover?: string;
        subtle?: string;
        medium?: string;
      };
      overlays?: {
        // Black overlays
        light?: string;
        medium?: string;
        dark?: string;
        darker?: string;
        // White overlays
        whiteLight?: string;
        whiteMedium?: string;
        whiteStrong?: string;
        whiteAlmost?: string;
        whiteFull?: string;
      };
    };
  }
}

// Font family constants for easy reuse
export const FONT_FAMILY_SERIF = [
  '"Cormorant Garamond"',
  '"Playfair Display"',
  "Georgia",
  "serif",
].join(",");

export const FONT_FAMILY_SANS = [
  '"Inter"',
  '"SF Pro Display"',
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI Variable"',
  '"Segoe UI"',
  "system-ui",
  "sans-serif",
].join(",");

export const FONT_FAMILY_SCRIPT = [
  '"Allura"',
  '"Great Vibes"',
  '"Dancing Script"',
  "cursive",
].join(",");

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1a1a1a", // Dark gray (softer than pure black)
      light: "#404040",
      dark: "#0d0d0d",
      contrastText: "#fafafa",
    },
    secondary: {
      main: "#f5f5f5", // Off-white (softer than pure white)
      light: "#fafafa",
      dark: "#e0e0e0",
      contrastText: "#1a1a1a",
    },
    background: {
      default: "#fafafa", // Soft off-white background (reduces eye strain)
      paper: "#ffffff", // White for cards/dialogs (provides subtle depth)
    },
    text: {
      primary: "#1a1a1a", // Dark gray for primary text (87% opacity equivalent)
      secondary: "#757575", // Medium gray for secondary text (60% opacity equivalent)
    },
    divider: "#e0e0e0", // Light gray dividers
  },
  // Custom theme values for common patterns
  custom: {
    transitions: {
      standard: "0.3s ease",
      fast: "0.2s ease",
      slow: "0.5s ease",
      medium: "0.4s ease",
      easeInOut: "0.2s ease-in-out",
    },
    shadows: {
      card: "0 8px 24px rgba(0, 0, 0, 0.15)",
      cardHover: "0 12px 32px rgba(0, 0, 0, 0.2)",
      subtle: "0 2px 8px rgba(0, 0, 0, 0.1)",
      medium: "0 4px 12px rgba(0, 0, 0, 0.12)",
    },
    overlays: {
      // Black overlays
      light: "rgba(0, 0, 0, 0.05)",
      medium: "rgba(0, 0, 0, 0.2)",
      dark: "rgba(0, 0, 0, 0.5)",
      darker: "rgba(0, 0, 0, 0.7)",
      // White overlays
      whiteLight: "rgba(255, 255, 255, 0.1)",
      whiteMedium: "rgba(255, 255, 255, 0.5)",
      whiteStrong: "rgba(255, 255, 255, 0.85)",
      whiteAlmost: "rgba(255, 255, 255, 0.9)",
      whiteFull: "rgba(255, 255, 255, 1)",
    },
  },
  typography: {
    fontFamily: FONT_FAMILY_SANS, // Default to sans-serif for UI
    h1: {
      fontFamily: FONT_FAMILY_SERIF, // Serif for elegant headings
      fontSize: "2.25rem", // 36px
      fontWeight: 300,
      letterSpacing: "-0.01em",
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: FONT_FAMILY_SERIF,
      fontSize: "1.75rem", // 28px
      fontWeight: 300,
      letterSpacing: "-0.005em",
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: FONT_FAMILY_SERIF,
      fontSize: "1.375rem", // 22px
      fontWeight: 400,
      letterSpacing: "0em",
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: FONT_FAMILY_SANS,
      fontSize: "1.125rem", // 18px
      fontWeight: 600,
      letterSpacing: "-0.005em",
    },
    h5: {
      fontFamily: FONT_FAMILY_SANS,
      fontSize: "1rem", // 16px
      fontWeight: 600,
      letterSpacing: "0em",
    },
    h6: {
      fontFamily: FONT_FAMILY_SANS,
      fontSize: "0.875rem", // 14px
      fontWeight: 600,
      letterSpacing: "0.02em",
    },
    body1: {
      fontFamily: FONT_FAMILY_SANS,
      fontSize: "1rem", // 16px
      fontWeight: 400,
      letterSpacing: "-0.011em",
      lineHeight: 1.7,
    },
    body2: {
      fontFamily: FONT_FAMILY_SANS,
      fontSize: "0.875rem", // 14px
      fontWeight: 400,
      letterSpacing: "-0.006em",
      lineHeight: 1.6,
    },
    caption: {
      fontFamily: FONT_FAMILY_SANS,
      fontSize: "0.75rem", // 12px
      fontWeight: 500,
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Global placeholder styling
        "input::placeholder, textarea::placeholder": {
          fontSize: "0.625rem", // 10px - smaller than helper text
          color: "#9e9e9e", // Lighter gray
          fontStyle: "italic",
          opacity: "1", // Override MUI's default opacity
        },
        // Global helper text styling
        ".MuiFormHelperText-root": {
          fontSize: "0.6875rem", // 11px - even smaller
          color: "#9e9e9e", // Lighter gray
          fontStyle: "italic",
          marginTop: "4px",
        },
        // Global input label styling - remove overflow hidden
        ".MuiFormLabel-root, .MuiInputLabel-root": {
          overflow: "visible",
        },
        // Override MUI generated classes that apply overflow hidden
        "[class*='MuiFormLabel-root'][class*='MuiInputLabel-root']": {
          overflow: "visible !important",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "text",
      },
      styleOverrides: {
        root: {
          fontFamily: "var(--button-font-family, 'Inter', sans-serif)",
          textTransform: "none",
          borderRadius: "8px",
          fontWeight: 500,
          padding: "10px 24px",
          fontSize: "0.9375rem",
          letterSpacing: "0.02em",
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "transparent !important",
            border: "none",
            boxShadow: "none",
          },
          "&:active": {
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
          },
          "&:focus": {
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
          },
        },
        contained: {
          backgroundColor: "transparent",
          color: "#1a1a1a",
          border: "none",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "transparent !important",
            border: "none",
            boxShadow: "none",
          },
        },
        text: {
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "transparent !important",
            border: "none",
            boxShadow: "none",
          },
        },
        outlined: {
          border: "none",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "transparent !important",
            border: "none",
            boxShadow: "none",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          border: "none",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "transparent !important",
            border: "none",
            boxShadow: "none",
          },
          "&:active": {
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
          },
          "&:focus": {
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          "&:hover": {
            textDecoration: "none",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: "#1a1a1a",
          boxShadow: "none",
          borderBottom: "1px solid #e0e0e0",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "12px", // Rounded dialog
          border: "1px solid #e0e0e0",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "standard", // Use standard variant (underline only)
        multiline: false, // Default to single line
        rows: 1, // Default to 1 row
      },
      styleOverrides: {
        root: {
          paddingTop: "8px", // Add padding to give labels more space
          "& .MuiInput-underline:before": {
            borderBottom: "1px solid #e0e0e0", // Add bottom border
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottom: "1px solid #bdbdbd", // Darker on hover
          },
          "& .MuiInput-underline:after": {
            borderBottom: "2px solid #1a1a1a", // Thicker and darker on focus
          },
          // Apply input font family
          "& .MuiInputBase-input": {
            fontFamily: '"Source Sans Pro", sans-serif',
          },
          // Label styling - make it look like placeholder text when not focused
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem", // 14px - same as placeholder
            color: "#9e9e9e", // Lighter gray like placeholder
            fontStyle: "italic",
            fontFamily: '"Source Sans Pro", sans-serif',
            "&.Mui-focused": {
              color: "#1a1a1a", // Darker when focused
              fontStyle: "normal", // Normal when focused
            },
            "&.MuiFormLabel-filled": {
              color: "#1a1a1a", // Darker when filled
              fontStyle: "normal", // Normal when filled
            },
            // Ensure proper positioning when shrunken
            "&.MuiInputLabel-shrink": {
              transform: "translate(0, -1.5px) scale(0.75)",
              transformOrigin: "top left",
              left: "0px",
              width: "auto",
              maxWidth: "none",
              whiteSpace: "nowrap",
              overflow: "visible",
            },
          },
          // Force single row for multiline TextFields by default
          "&:not([data-allow-multiline='true']) .MuiInputBase-multiline": {
            minHeight: "1.4375em",
            height: "1.4375em",
            "& .MuiInputBase-input, & textarea": {
              minHeight: "1.4375em",
              height: "1.4375em",
              maxHeight: "1.4375em", // Prevent expansion
              lineHeight: "1.4375em",
              padding: "8px 0", // Match single line input padding
            },
            "& textarea": {
              resize: "none",
            },
          },
          // Allow override for specific cases where multiple rows are needed
          "&[data-allow-multiline='true'] .MuiInputBase-multiline": {
            minHeight: "auto",
            height: "auto",
            "& .MuiInputBase-input, & textarea": {
              minHeight: "auto",
              height: "auto",
              maxHeight: "none",
              overflow: "auto",
              resize: "vertical",
              lineHeight: "normal",
              padding: "12px 0", // More vertical padding for multiline
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          // Remove borders globally
          "& .MuiInput-underline:before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:after": {
            borderBottom: "none",
          },
          // Apply input font family
          "& .MuiSelect-select": {
            fontFamily: '"Source Sans Pro", sans-serif',
          },
          // Label styling - make it look like input labels
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem", // 14px - same as input labels
            color: "#9e9e9e", // Lighter gray like placeholder
            fontStyle: "italic",
            overflow: "visible", // Remove overflow hidden
            fontFamily: '"Source Sans Pro", sans-serif',
            "&.Mui-focused": {
              color: "#1a1a1a", // Darker when focused
              fontStyle: "normal", // Normal when focused
            },
            "&.MuiFormLabel-filled": {
              color: "#1a1a1a", // Darker when filled
              fontStyle: "normal", // Normal when filled
            },
            // Ensure proper positioning when shrunken
            "&.MuiInputLabel-shrink": {
              transform: "translate(0, -1.5px) scale(0.75)",
              transformOrigin: "top left",
              left: "0px",
              width: "auto",
              maxWidth: "none",
              whiteSpace: "nowrap",
              overflow: "visible",
            },
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          // Remove borders globally
          "& .MuiInput-underline:before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
            borderBottom: "none",
          },
          "& .MuiInput-underline:after": {
            borderBottom: "none",
          },
          // Apply input font family
          "& .MuiInputBase-input, & .MuiSelect-select": {
            fontFamily: '"Source Sans Pro", sans-serif',
          },
          // Apply label styling to all form controls including Select
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem", // 14px - same as input labels
            color: "#9e9e9e", // Lighter gray like placeholder
            fontStyle: "italic",
            overflow: "visible", // Remove overflow hidden
            fontFamily: '"Source Sans Pro", sans-serif',
            "&.Mui-focused": {
              color: "#1a1a1a", // Darker when focused
              fontStyle: "normal", // Normal when focused
            },
            "&.MuiFormLabel-filled": {
              color: "#1a1a1a", // Darker when filled
              fontStyle: "normal", // Normal when filled
            },
            // Ensure proper positioning when shrunken
            "&.MuiInputLabel-shrink": {
              transform: "translate(0, -1.5px) scale(0.75)",
              transformOrigin: "top left",
              left: "0px",
              width: "auto",
              maxWidth: "none",
              whiteSpace: "nowrap",
              overflow: "visible",
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          opacity: "1 !important",
          pointerEvents: "auto",
          "& .MuiSvgIcon-root": {
            opacity: "1 !important",
          },
        },
      },
    },
  },
});
